import React, { useCallback, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import ExcelImage from "./ExcelImage";
import ExcelChart from "./ExcelChart";
import { PANES, computePane } from "./paneClip";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";
import "./Overlays.css";

/**
 * VisualOverlay
 * Absolutely-positioned layer over the grid that renders embedded images
 * and charts.  Keyed on the active sheet so children fully unmount /
 * remount when the user switches sheets.
 *
 * Freeze panes: when rows and/or columns are frozen, every object is
 * rendered once per pane it overlaps (body / frozen-row / frozen-col /
 * corner).  The pane copies are positioned + clipped IMPERATIVELY by a
 * scroll handler (writing straight to the DOM, no React re-render) so the
 * frozen-band copies stay perfectly pinned to the freeze line while the
 * body scrolls — smooth even on chart-heavy sheets.  React only renders
 * the copies once (at rest); the handler takes over on scroll.
 */
function VisualOverlay({
  activeSheet, sheetImages, sheetCharts, colWidths, rowHeights, zoomScale,
  hiddenCols, hiddenRows, frozenRows = 0, frozenCols = 0, wrapperRef,
}) {
  const overlayRef = useRef(null);

  // Freeze lines in CONTENT px (skip hidden rows/cols, matching the grid).
  const { freezeX, freezeY } = useMemo(() => {
    let fx = HEADER_COL_WIDTH;
    for (let i = 0; i < frozenCols; i++) {
      if (!(hiddenCols && hiddenCols.has && hiddenCols.has(i))) fx += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    let fy = HEADER_ROW_HEIGHT;
    for (let i = 0; i < frozenRows; i++) {
      if (!(hiddenRows && hiddenRows.has && hiddenRows.has(i))) fy += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }
    return { freezeX: fx, freezeY: fy };
  }, [frozenCols, frozenRows, colWidths, rowHeights, hiddenCols, hiddenRows]);

  const hasFreeze = frozenRows > 0 || frozenCols > 0;
  const panes = hasFreeze ? PANES : ["body"];

  // Reposition + re-clip every pane copy for the current scroll offset,
  // writing directly to the DOM (no React state → no reconciliation lag).
  const applyPanes = useCallback(() => {
    const ov = overlayRef.current;
    if (!ov) return;
    const wrap = wrapperRef && wrapperRef.current;
    const z = zoomScale || 1;
    const sx = (wrap ? wrap.scrollLeft : 0) / z;
    const sy = (wrap ? wrap.scrollTop : 0) / z;
    const nodes = ov.querySelectorAll("[data-pane]");
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      const pane = n.dataset.pane;
      const L = +n.dataset.cl, T = +n.dataset.ct, W = +n.dataset.cw, H = +n.dataset.ch;
      const g = computePane(pane, L, T, W, H, freezeX, freezeY, sx, sy);
      if (!g) { n.style.display = "none"; continue; }
      n.style.display = "";
      n.style.left = (L + g.offsetX) + "px";
      n.style.top = (T + g.offsetY) + "px";
      n.style.clipPath = g.clipPath || "none";
      n.style.webkitClipPath = g.clipPath || "none";
    }
  }, [wrapperRef, zoomScale, freezeX, freezeY]);

  // Live updates: re-pin on every scroll frame (rAF-throttled, imperative).
  useEffect(() => {
    const wrap = wrapperRef && wrapperRef.current;
    if (!wrap) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; applyPanes(); });
    };
    wrap.addEventListener("scroll", onScroll, { passive: true });
    applyPanes();
    return () => {
      wrap.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [applyPanes, activeSheet]);

  // Re-pin after every structural render (React resets the inline styles to
  // their rest positions; immediately re-apply for the current scroll).
  useLayoutEffect(() => { applyPanes(); });

  return (
    <div
      key={activeSheet}
      ref={overlayRef}
      className="excel-visual-content-overlay"
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zoom: zoomScale }}
    >
      {sheetImages.map((image, index) =>
        panes.map((pane) => (
          <div key={`image-${index}-${pane}`} style={{ pointerEvents: "auto" }}>
            <ExcelImage
              image={image} pane={pane}
              colWidths={colWidths} rowHeights={rowHeights} zoomScale={1}
              hiddenCols={hiddenCols} hiddenRows={hiddenRows}
              freezeX={freezeX} freezeY={freezeY} scrollX={0} scrollY={0}
            />
          </div>
        ))
      )}
      {sheetCharts.map((chart, index) =>
        panes.map((pane) => (
          <div key={`chart-${index}-${pane}`} style={{ pointerEvents: "auto" }}>
            <ExcelChart
              chart={chart} pane={pane}
              colWidths={colWidths} rowHeights={rowHeights} zoomScale={1}
              hiddenCols={hiddenCols} hiddenRows={hiddenRows}
              freezeX={freezeX} freezeY={freezeY} scrollX={0} scrollY={0}
            />
          </div>
        ))
      )}
    </div>
  );
}

export default React.memo(VisualOverlay);
